/**
 * Wiki access choices ↔ access rules (#871), as pure helpers so the create
 * dialog, the Access panel and their tests share one mapping.
 *
 * The server stays a generic rules store (`wiki_v2_acl`, `PUT …/acl`): this
 * module turns the admin's choices into rows and back. Rules resolve most
 * specific scope first (page → subcategory → category → role default), and a
 * scope with ANY matching row stops the walk. So a customised scope gets a
 * row for every role, and the nearest ancestor's per-person rows are carried
 * forward (otherwise those people would silently lose their grant).
 *
 * Levels: none < view < edit < admin. `user` covers `sales_rep` (alias).
 */

/** The roles the Access editor writes, in display order. */
export const WIKI_ACL_ROLES = Object.freeze(['admin', 'developer', 'manager', 'user']);

/** Role names a person's role may come back as from `GET /wiki/members`. */
const ROLE_ALIAS = Object.freeze({ sales_rep: 'user' });

const RANK = Object.freeze({ none: 0, view: 1, edit: 2, admin: 3 });

export const WIKI_ACCESS_COPY = Object.freeze({
    viewLabel: 'WHO CAN VIEW',
    editLabel: 'WHO CAN EDIT',
    view: Object.freeze({
        parent: (parentName) => `Same as ${parentName || 'parent'}`,
        everyone: 'Everyone',
        roles: 'Specific roles',
    }),
    edit: Object.freeze({
        parent: (parentName) => `Same as ${parentName || 'parent'}`,
        editors: 'Editors and admins',
        admins: 'Wiki admins only',
        people: 'Specific people',
    }),
    roleLabels: Object.freeze({
        admin: 'Admins',
        developer: 'Developers',
        manager: 'Managers',
        user: 'Members',
    }),
    adminsAlways: 'Admins always have access',
    rolesHint: 'Only the checked roles can see it in the Wiki, in search and in Ask the Wiki answers.',
    rolesNeedOne: 'Choose at least one role besides Admins.',
    peopleHint: 'Wiki admins can always edit. Only members who can edit the Wiki are listed.',
    peopleSearch: 'Find a person',
    peopleEmpty: 'No one matches.',
    peopleNeedOne: 'Choose at least one person.',
    peopleLoading: 'Loading people…',
    alreadyEdits: 'Already edits',
    roleCantView: 'Their role can\'t view it, but this rule lets them.',
    notCarried: (names) => `Not carried over from the parent: ${names}. Their role can't view this.`,
    defaultNoteTop: 'Uses the default access: everyone can view, editors and admins can edit. A Wiki admin can change it later.',
    defaultNoteSub: (parentName) => `Uses ${parentName || 'the parent'}'s access. A Wiki admin can change it later.`,
});

/** The dialog's starting choices: role defaults (top) or inherit (sub/page). */
export function defaultAccessChoices(nested) {
    return nested
        ? { view: 'parent', roles: ['admin'], edit: 'parent', people: [] }
        : { view: 'everyone', roles: ['admin'], edit: 'editors', people: [] };
}

/** True when the choices write no rows (role defaults, or inherit). */
export function isDefaultAccess(choices, nested) {
    if (!choices) return true;
    if (nested) return choices.view === 'parent' && choices.edit === 'parent';
    return choices.view === 'everyone' && choices.edit === 'editors';
}

/**
 * Whether the choices can be saved yet: a "Specific …" choice needs a pick,
 * any custom choice needs the server's role floors (`GET /wiki/members`
 * `role_floors`), and an inherited half needs the ancestor's summary.
 */
export function accessChoicesReady(choices, { nested = false, ancestorSummary = null, roleFloors = null } = {}) {
    if (!choices || isDefaultAccess(choices, nested)) return true;
    if (choices.view === 'roles' && !(choices.roles || []).some((role) => role !== 'admin')) return false;
    if (choices.edit === 'people' && !(choices.people || []).length) return false;
    if (!roleFloors) return false;
    const usesParent = choices.view === 'parent' || choices.edit === 'parent';
    return !usesParent || Boolean(ancestorSummary && ancestorSummary.role_levels);
}

/**
 * Per-role level the choices give, before people rows. `floors` is each
 * role's level before any rule, from the server (`role_floors`): the editor
 * never keeps its own copy of the RBAC defaults.
 */
function roleLevels(choices, ancestorSummary, floors) {
    const floorOf = (role) => floors[role] || 'none';
    const parentLevels = (ancestorSummary && ancestorSummary.role_levels) || {};
    const chosen = new Set(['admin', ...(choices.roles || [])]);
    const out = {};
    WIKI_ACL_ROLES.forEach((role) => {
        let viewable;
        if (choices.view === 'everyone') viewable = true;
        else if (choices.view === 'roles') viewable = chosen.has(role);
        else viewable = Boolean(parentLevels[role]);
        if (!viewable) {
            out[role] = 'none';
            return;
        }
        let level;
        if (choices.edit === 'parent') {
            level = parentLevels[role] || 'view';
        } else if (choices.edit === 'editors') {
            level = floorOf(role);
        } else {
            // Wiki admins only, and Specific people (#871 D10): roles whose
            // floor is admin (admins, developers) keep it; everyone else views.
            level = floorOf(role) === 'admin' ? 'admin' : 'view';
        }
        // Never above what the role's permissions allow.
        out[role] = RANK[level] > RANK[floorOf(role)] ? floorOf(role) : level;
    });
    return out;
}

function memberRole(member) {
    const role = member && member.role;
    return ROLE_ALIAS[role] || role || 'user';
}

/**
 * Choices → scope-less rules `{subject_type, subject_id, level}`.
 *
 * Returns `{ rules, notCarried }`. `rules` is `[]` for default/inherit
 * choices. `notCarried` lists the ancestor's people who were dropped because
 * this scope's view choice excludes their role (copying their grant would
 * re-open the restriction).
 */
export function choicesToRules(choices, {
    nested = false,
    ancestorSummary = null,
    ancestorRows = [],
    members = [],
    roleFloors = null,
} = {}) {
    if (isDefaultAccess(choices, nested) || !roleFloors) return { rules: [], notCarried: [] };
    const levels = roleLevels(choices, ancestorSummary, roleFloors);
    const rules = WIKI_ACL_ROLES.map((role) => ({ subject_type: 'role', subject_id: role, level: levels[role] }));
    const picked = choices.edit === 'people' ? [...new Set(choices.people || [])] : [];
    picked.forEach((userId) => rules.push({ subject_type: 'user', subject_id: userId, level: 'edit' }));

    const byId = new Map((members || []).map((member) => [member.user_id, member]));
    const notCarried = [];
    (ancestorRows || [])
        .filter((row) => row && row.subject_type === 'user' && !picked.includes(row.subject_id))
        .forEach((row) => {
            const member = byId.get(row.subject_id);
            if (!member) return; // inactive or gone: the server would refuse it
            const roleLevel = levels[memberRole(member)] || 'none';
            if (roleLevel === 'none' && row.level !== 'none') {
                notCarried.push(member.name || member.email || row.subject_id);
                return;
            }
            // With its own edit choice this scope redefines editors, so a
            // carried grant keeps visibility only; with "Same as parent" it
            // keeps its level.
            let level = row.level;
            if (choices.edit !== 'parent' && RANK[level] > RANK.view) level = 'view';
            // Only a row that changes the outcome is worth carrying.
            const grants = RANK[level] > RANK[roleLevel];
            if (grants || level === 'none') {
                rules.push({ subject_type: 'user', subject_id: row.subject_id, level });
            }
        });
    return { rules, notCarried };
}

/**
 * Stored rules → choices, or `null` when the rules aren't something the
 * choices can express (hand-made API rules): the panel then shows them in
 * the Advanced list and saves them as they are.
 */
export function rulesToChoices(rows, { nested = false } = {}) {
    const list = (rows || []).filter(Boolean);
    if (!list.length) return defaultAccessChoices(nested);
    const roleRows = {};
    const people = [];
    for (const row of list) {
        if (row.subject_type === 'role') {
            const role = ROLE_ALIAS[row.subject_id] || row.subject_id;
            if (!WIKI_ACL_ROLES.includes(role) || roleRows[role]) return null;
            roleRows[role] = row.level;
        } else if (row.subject_type === 'user') {
            if (row.level !== 'edit') return null;
            people.push(row.subject_id);
        } else {
            return null;
        }
    }
    if (!WIKI_ACL_ROLES.every((role) => roleRows[role])) return null;
    // Admins always keep admin; developers keep it unless "Specific roles"
    // left them out (then they can't view at all).
    if (roleRows.admin !== 'admin' || !['admin', 'none'].includes(roleRows.developer)) return null;
    if (!['none', 'view'].includes(roleRows.user)) return null;
    if (!['none', 'view', 'edit'].includes(roleRows.manager)) return null;

    const viewRoles = WIKI_ACL_ROLES.filter((role) => roleRows[role] !== 'none');
    const view = viewRoles.length === WIKI_ACL_ROLES.length ? 'everyone' : 'roles';
    let edit;
    if (roleRows.manager === 'edit') {
        if (people.length) return null;
        edit = 'editors';
    } else {
        edit = people.length ? 'people' : 'admins';
    }
    return { view, roles: view === 'roles' ? viewRoles : ['admin'], edit, people };
}

function joinLabels(labels) {
    if (labels.length <= 1) return labels.join('');
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function sentenceCase(text) {
    return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : text;
}

function withPeople(text, count) {
    if (!count) return text;
    const people = count === 1 ? '1 person' : `${count} people`;
    return text ? `${text} and ${people}` : sentenceCase(people);
}

/**
 * "Everyone can view · Editors and admins can edit" from `access_summary`
 * (#871): who can ACTUALLY view and edit, resolved by the server from the
 * rules and role defaults. '' while the summary isn't loaded.
 */
export function accessLine(summary) {
    if (!summary) return '';
    const labels = WIKI_ACCESS_COPY.roleLabels;
    const viewRoles = summary.view_roles || [];
    const editRoles = summary.edit_roles || [];
    let view;
    if (WIKI_ACL_ROLES.every((role) => viewRoles.includes(role))) view = 'Everyone';
    else view = sentenceCase(joinLabels(viewRoles.map((role) => labels[role])));
    view = withPeople(view, summary.view_people) || 'Only org admins';

    let edit;
    const has = (role) => editRoles.includes(role);
    if (has('admin') && has('developer') && has('manager') && editRoles.length === 3) edit = 'Editors and admins';
    else if (has('admin') && has('developer') && editRoles.length === 2) edit = 'Wiki admins';
    else edit = sentenceCase(joinLabels(editRoles.map((role) => labels[role])));
    edit = withPeople(edit, summary.edit_people) || 'Only org admins';
    return `${view} can view · ${edit} can edit`;
}

/** The nearest ancestor scope whose own rules apply, from a parent category view. */
export function ancestorRulesCategoryId(parentDetail) {
    const summary = parentDetail && parentDetail.access_summary;
    if (!summary) return null;
    if (summary.source === 'own') return parentDetail.category_id || null;
    if (summary.source === 'parent') return parentDetail.parent_id || null;
    return null;
}
